import { config } from '../config';
import { auth } from '../config/firebase';

const API_BASE_URL = config.api.baseURL;

/**
 * Get Firebase auth token
 */
const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return await user.getIdToken();
};

export interface UserProfile {
  id: string;
  firebaseUid: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'super_admin';
  isActive: boolean;
  default?: boolean;
  lastLogin?: string;
  preferences?: {
    theme: 'light' | 'dark';
    notifications: {
      email: boolean;
      push: boolean;
    };
    tutorialCompleted?: boolean;
  };
  metadata?: Record<string, any>;
  calendarSettings?: {
    autoJoinEnabled: boolean;
    minutesBeforeStart: number;
    enabledPlatforms: Array<'meet' | 'zoom' | 'teams'>;
    notifyOnFailure: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Get current user profile
 */
export const getCurrentUser = async (): Promise<UserProfile> => {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch user profile');
  }

  const data = await response.json();
  return data.data.user;
};

export interface AdminVerificationResult {
  isAdmin: boolean;
  user: UserProfile | null;
}

/**
 * Check if user has admin role and return user data
 */
export const checkAdminRole = async (): Promise<AdminVerificationResult> => {
  try {
    const token = await getAuthToken();

    // Use the dedicated admin verification endpoint
    const response = await fetch(`${API_BASE_URL}/api/admin/verify-admin-access`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Admin verification failed:', error);
      return { isAdmin: false, user: null };
    }

    const data = await response.json();
    console.log('data is', data);

    const role = data.data?.user?.role;
    // Both 'admin' and 'super_admin' are valid portal roles
    const isAdmin = data.success && (role === 'admin' || role === 'super_admin');
    const user = data.data?.user || null;

    return { isAdmin, user };
  } catch (error) {
    console.error('Failed to check admin role:', error);
    return { isAdmin: false, user: null };
  }
};